import React, { useState, useEffect, useMemo } from 'react';
import { Users, Download, Search, RefreshCw, UserCheck, ShoppingBag, Clock, AlertCircle, CreditCard } from 'lucide-react';
import * as XLSX from 'xlsx';

const API_BASE_URL = 'https://apisaleeasy.psisat.com';
const CACHE_KEY = 'salesman_customer_summary';

const SalesmanCustomerCountComponent = () => {
  const [activeTab, setActiveTab] = useState('summary');

  // Tab 1: summary
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [updatedAt, setUpdatedAt] = useState(null);
  const [updatedBy, setUpdatedBy] = useState('');
  const [searchText, setSearchText] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'customer_count', dir: 'desc' });

  // Tab 2: ลูกค้าหาย 3 เดือน (lazy from detail cache)
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [inactiveSearch, setInactiveSearch] = useState('');

  // Tab 3: มีเครดิตไม่ซื้อ
  const [creditData, setCreditData] = useState(null);
  const [creditLoading, setCreditLoading] = useState(false);
  const [creditError, setCreditError] = useState('');
  const [creditSearch, setCreditSearch] = useState('');

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/report-cache/${CACHE_KEY}`);
      if (res.status === 404) {
        await triggerRefresh(true);
        return;
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (!json.success) throw new Error(json.message || 'API error');
      setData(json.data || []);
      setUpdatedAt(json.updated_at);
      setUpdatedBy(json.updated_by);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchDetailData = async () => {
    if (detailData) return;
    setDetailLoading(true);
    setDetailError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/report-cache/salesman_customer_summary/detail`);
      const json = await res.json();
      if (json.not_ready) throw new Error(json.message);
      if (!json.success) throw new Error(json.message || 'Detail API error');
      setDetailData(json.data || []);
    } catch (err) {
      setDetailError(err.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const fetchCreditData = async () => {
    if (creditData) return;
    setCreditLoading(true);
    setCreditError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/report-cache/credit_customer_inactive`);
      if (res.status === 404) {
        // ยังไม่มี cache — trigger refresh แล้วรอ
        const trigRes = await fetch(`${API_BASE_URL}/api/report-cache/credit_customer_inactive/refresh`, { method: 'POST' });
        const trigJson = await trigRes.json();
        if (!trigJson.success) throw new Error(trigJson.message || 'Refresh failed');
        // reload from cache
        const res2 = await fetch(`${API_BASE_URL}/api/report-cache/credit_customer_inactive`);
        const json2 = await res2.json();
        setCreditData(json2.data || []);
        return;
      }
      const json = await res.json();
      if (!json.success) throw new Error(json.message || 'Credit API error');
      setCreditData(json.data || []);
    } catch (err) {
      setCreditError(err.message);
    } finally {
      setCreditLoading(false);
    }
  };

  const triggerRefresh = async (silent = false) => {
    if (!silent) setRefreshing(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE_URL}/api/report-cache/${CACHE_KEY}/refresh`, { method: 'POST' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      if (!json.success) throw new Error(json.message || 'Refresh failed');
      // reset lazy caches
      setDetailData(null);
      setCreditData(null);
      await fetchData();
    } catch (err) {
      setError(err.message);
      if (!silent) setRefreshing(false);
    } finally {
      if (!silent) setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (activeTab === 'inactive') fetchDetailData();
    if (activeTab === 'credit') fetchCreditData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleSort = (key) => {
    setSortConfig(prev =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'desc' }
    );
  };

  const filtered = useMemo(() => {
    let rows = data;
    if (searchText.trim()) {
      const q = searchText.trim().toLowerCase();
      rows = rows.filter(r =>
        r.salesman_name.toLowerCase().includes(q) ||
        r.salesman_code.toLowerCase().includes(q)
      );
    }
    return [...rows].sort((a, b) => {
      const av = a[sortConfig.key];
      const bv = b[sortConfig.key];
      if (av === null || av === undefined) return 1;
      if (bv === null || bv === undefined) return -1;
      if (typeof av === 'number') return sortConfig.dir === 'asc' ? av - bv : bv - av;
      return sortConfig.dir === 'asc'
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
  }, [data, searchText, sortConfig]);

  // Tab 2: กรองลูกค้าที่ไม่มียอด 90 วัน (deduplicate by customer — ใช้ last_sale_date ล่าสุดทุกเซลล์)
  const inactiveRows = useMemo(() => {
    if (!detailData) return [];
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 90);

    // หา last_sale_date ล่าสุดของลูกค้าแต่ละคน (ทุกเซลล์รวมกัน)
    const customerMap = {};
    detailData.forEach(r => {
      const code = r.customer_code?.trim();
      if (!code) return;
      const existing = customerMap[code];
      if (!existing) {
        customerMap[code] = { ...r, last_salesman_code: r.salesman_code, last_salesman_name: r.salesman_name };
      } else {
        // ถ้า row นี้ซื้อล่าสุดกว่า ให้อัปเดต last_sale_date + เซลล์คนนั้น
        if (r.last_sale_date && (!existing.last_sale_date || new Date(r.last_sale_date) > new Date(existing.last_sale_date))) {
          existing.last_sale_date = r.last_sale_date;
          existing.last_salesman_code = r.salesman_code;
          existing.last_salesman_name = r.salesman_name;
        }
        existing.invoice_count += r.invoice_count || 0;
        existing.total_amount += r.total_amount || 0;
      }
    });

    return Object.values(customerMap)
      .filter(r => r.phone && r.phone.trim())
      .filter(r => r.last_sale_date && new Date(r.last_sale_date) < cutoff)
      .map(r => ({
        ...r,
        days_inactive: Math.floor((Date.now() - new Date(r.last_sale_date)) / 86400000)
      }))
      .filter(r => {
        if (!inactiveSearch.trim()) return true;
        const q = inactiveSearch.trim().toLowerCase();
        return (
          r.customer_name?.toLowerCase().includes(q) ||
          r.customer_code?.toLowerCase().includes(q)
        );
      })
      .sort((a, b) => b.days_inactive - a.days_inactive);
  }, [detailData, inactiveSearch]);

  // Tab 3: กรองลูกค้ามีเครดิตไม่ซื้อ
  const creditFiltered = useMemo(() => {
    if (!creditData) return [];
    const withPhone = creditData.filter(r => r.phone && r.phone.trim());
    if (!creditSearch.trim()) return withPhone;
    const q = creditSearch.trim().toLowerCase();
    return withPhone.filter(r =>
      r.customer_name?.toLowerCase().includes(q) ||
      r.customer_code?.toLowerCase().includes(q)
    );
  }, [creditData, creditSearch]);

  const totalCustomers = useMemo(() => filtered.reduce((acc, r) => acc + r.customer_count, 0), [filtered]);
  const totalInvoices = useMemo(() => filtered.reduce((acc, r) => acc + r.total_invoices, 0), [filtered]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('th-TH', { year: 'numeric', month: 'short', day: 'numeric' });
  };
  const formatNumber = (n) => Number(n).toLocaleString('th-TH');
  const formatAmount = (n) =>
    Number(n).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // ── Export Tab 1 (per-salesman sheets, monthly = ยอดซื้อ) ──
  const handleExportSummary = async () => {
    setExporting(true);
    try {
      const summaryRows = filtered.map((r, i) => ({
        'ลำดับ': i + 1,
        'รหัสเซลล์': r.salesman_code,
        'ชื่อเซลล์': r.salesman_name,
        'สาขา': r.branch_code || '-',
        'จำนวนลูกค้า (ราย)': r.customer_count,
        'จำนวนบิล': r.total_invoices,
        'ยอดขายรวม (บาท)': r.total_amount,
        'วันที่ขายล่าสุด': formatDate(r.last_sale_date)
      }));

      const [detailRes, monthlyRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/report-cache/salesman_customer_summary/detail`),
        fetch(`${API_BASE_URL}/api/report-cache/salesman_customer_summary/monthly`)
      ]);
      const [detailJson, monthlyJson] = await Promise.all([detailRes.json(), monthlyRes.json()]);

      if (detailJson.not_ready) throw new Error(detailJson.message);
      if (!detailJson.success) throw new Error(detailJson.message || 'Detail API error');
      if (monthlyJson.not_ready) throw new Error(monthlyJson.message);
      if (!monthlyJson.success) throw new Error(monthlyJson.message || 'Monthly API error');

      const thaiMonths = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
                          'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth();
      const buddhistYearShort = String(currentYear + 543).slice(-2);
      const monthKeys = [];
      const monthLabels = [];
      for (let m = 0; m <= currentMonth; m++) {
        const mm = String(m + 1).padStart(2, '0');
        monthKeys.push(`${currentYear}-${mm}`);
        monthLabels.push(`${thaiMonths[m]}${buddhistYearShort}`);
      }

      // Lookup: "salesmanCode|customerCode|2026-01" → total_amount (ยอดซื้อ)
      const monthlyLookup = {};
      for (const r of (monthlyJson.data || [])) {
        monthlyLookup[`${r.salesman_code}|${r.customer_code}|${r.month}`] = r.total_amount;
      }

      const filteredCodes = new Set(filtered.map(r => r.salesman_code.trim()));
      const detailBySalesman = {};
      for (const r of (detailJson.data || [])) {
        const code = r.salesman_code.trim();
        if (!filteredCodes.has(code)) continue;
        if (!detailBySalesman[code]) detailBySalesman[code] = [];
        detailBySalesman[code].push(r);
      }

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryRows), 'สรุปต่อเซลล์');

      const usedSheetNames = new Set(['สรุปต่อเซลล์']);
      for (const salesman of filtered) {
        const code = salesman.salesman_code.trim();
        const customers = detailBySalesman[code] || [];
        if (customers.length === 0) continue;

        let sheetName = `${code} ${salesman.salesman_name || ''}`.replace(/[/\\?*[\]:]/g, '').trim().slice(0, 31);
        let uniqueName = sheetName;
        let suffix = 2;
        while (usedSheetNames.has(uniqueName)) uniqueName = sheetName.slice(0, 28) + `(${suffix++})`;
        usedSheetNames.add(uniqueName);

        const rows = customers.map((r, i) => {
          const custCode = r.customer_code;
          const row = {
            'ลำดับ': i + 1,
            'รหัสลูกค้า': custCode,
            'ชื่อลูกค้า': r.customer_name || '-',
            'จังหวัด': r.province || '-',
            'เบอร์ติดต่อ': r.phone || '-',
          };
          let totalAmountYear = 0;
          for (let mi = 0; mi < monthKeys.length; mi++) {
            const amount = monthlyLookup[`${code}|${custCode}|${monthKeys[mi]}`] || 0;
            row[monthLabels[mi]] = amount || '';
            totalAmountYear += amount;
          }
          row['ยอดรวมปีนี้ (บาท)'] = totalAmountYear || '';
          row['บิลรวมทั้งหมด'] = r.invoice_count;
          row['ยอดรวมทั้งหมด (บาท)'] = r.total_amount;
          row['ซื้อล่าสุด'] = formatDate(r.last_sale_date);
          return row;
        });
        XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), uniqueName);
      }

      XLSX.writeFile(wb, 'salesman_customer_count.xlsx');
    } catch (err) {
      setError(`Export ไม่สำเร็จ: ${err.message}`);
    } finally {
      setExporting(false);
    }
  };

  // ── Export Tab 2 ──
  const handleExportInactive = () => {
    const rows = inactiveRows.map((r, i) => ({
      'ลำดับ': i + 1,
      'รหัสลูกค้า': r.customer_code,
      'ชื่อลูกค้า': r.customer_name || '-',
      'จังหวัด': r.province || '-',
      'เบอร์ติดต่อ': r.phone || '-',
      'รหัสเซลล์ล่าสุด': r.last_salesman_code || '-',
      'เซลล์ล่าสุด': r.last_salesman_name || '-',
      'ซื้อล่าสุด': formatDate(r.last_sale_date),
      'ห่างกี่วัน': r.days_inactive,
      'บิลรวมทั้งหมด': r.invoice_count,
      'ยอดรวม (บาท)': r.total_amount
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'ลูกค้าหาย 3 เดือน');
    XLSX.writeFile(wb, 'inactive_customers_90days.xlsx');
  };

  // ── Export Tab 3 ──
  const handleExportCredit = () => {
    const rows = creditFiltered.map((r, i) => ({
      'ลำดับ': i + 1,
      'รหัสลูกค้า': r.customer_code,
      'ชื่อลูกค้า': r.customer_name || '-',
      'จังหวัด': r.province || '-',
      'เบอร์ติดต่อ': r.phone || '-',
      'ประเภท': r.customer_type === 'ma' ? 'ตัวแทนรายใหญ่' : r.customer_type === 'new' ? 'ลูกค้าใหม่' : 'ทั่วไป',
      'วงเงินเครดิต (บาท)': r.credit_limit,
      'วงเงินคงเหลือ (บาท)': r.credit_remaining
    }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), 'ลูกค้ามีเครดิต');
    XLSX.writeFile(wb, 'credit_customers.xlsx');
  };

  const SortIcon = ({ col }) => {
    if (sortConfig.key !== col) return <span className="text-gray-300 ml-1">↕</span>;
    return <span className="text-blue-600 ml-1">{sortConfig.dir === 'asc' ? '↑' : '↓'}</span>;
  };

  const thClass = 'px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-800 whitespace-nowrap select-none';
  const thClassRight = thClass + ' text-right';

  const tabs = [
    { key: 'summary', label: 'สรุปต่อเซลล์', icon: UserCheck },
    { key: 'inactive', label: 'ลูกค้าหาย 3 เดือน', icon: AlertCircle },
    { key: 'credit', label: 'ลูกค้ามีเครดิต', icon: CreditCard }
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">จำนวนลูกค้าต่อเซลล์</h2>
          <p className="text-gray-500 text-sm mt-1">ข้อมูลสะสมทั้งหมด (all-time) — อัปเดตอัตโนมัติทุกคืน 02:00</p>
          {updatedAt && (
            <p className="text-xs text-gray-400 mt-0.5 flex items-center gap-1">
              <Clock className="w-3 h-3" />
              ข้อมูล ณ {new Date(updatedAt).toLocaleString('th-TH')}
              {updatedBy && <span className="ml-1 text-gray-300">({updatedBy})</span>}
            </p>
          )}
        </div>
        <button
          onClick={() => triggerRefresh(false)}
          disabled={loading || refreshing}
          title="Refresh ข้อมูล (ใช้เวลา ~5-10 วินาที)"
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'กำลัง Refresh...' : 'Refresh ข้อมูล'}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {tabs.map(tab => {
          const Icon = tab.icon;
          const active = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                active
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Global error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
          เกิดข้อผิดพลาด: {error}
        </div>
      )}

      {/* ─── TAB 1: สรุปต่อเซลล์ ─── */}
      {activeTab === 'summary' && (
        <>
          {!loading && data.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <UserCheck className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">จำนวนเซลล์ทั้งหมด</p>
                  <p className="text-2xl font-bold text-gray-900">{formatNumber(filtered.length)} คน</p>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">ลูกค้ารวม (นับซ้ำต่อเซลล์)</p>
                  <p className="text-2xl font-bold text-gray-900">{formatNumber(totalCustomers)} ราย</p>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <ShoppingBag className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">จำนวนบิลรวม</p>
                  <p className="text-2xl font-bold text-gray-900">{formatNumber(totalInvoices)} บิล</p>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="ค้นหารหัสหรือชื่อเซลล์..."
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={handleExportSummary}
              disabled={loading || exporting || filtered.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
            >
              <Download className={`w-4 h-4 ${exporting ? 'animate-bounce' : ''}`} />
              {exporting ? 'กำลัง Export...' : 'Export Excel'}
            </button>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
                <span className="ml-3 text-gray-600">กำลังโหลดข้อมูล...</span>
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                <Users className="w-12 h-12 mb-3" />
                <p className="text-lg font-medium">ไม่พบข้อมูล</p>
                <p className="text-sm">ลองปรับตัวกรองแล้วกดค้นหาใหม่</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-12">#</th>
                      <th className={thClass} onClick={() => handleSort('salesman_code')}>รหัสเซลล์ <SortIcon col="salesman_code" /></th>
                      <th className={thClass} onClick={() => handleSort('salesman_name')}>ชื่อเซลล์ <SortIcon col="salesman_name" /></th>
                      <th className={thClass} onClick={() => handleSort('branch_code')}>สาขา <SortIcon col="branch_code" /></th>
                      <th className={thClassRight} onClick={() => handleSort('customer_count')}>จำนวนลูกค้า <SortIcon col="customer_count" /></th>
                      <th className={thClassRight} onClick={() => handleSort('total_invoices')}>จำนวนบิล <SortIcon col="total_invoices" /></th>
                      <th className={thClassRight} onClick={() => handleSort('total_amount')}>ยอดขายรวม (บาท) <SortIcon col="total_amount" /></th>
                      <th className={thClass} onClick={() => handleSort('last_sale_date')}>ขายล่าสุด <SortIcon col="last_sale_date" /></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {filtered.map((row, i) => (
                      <tr key={row.salesman_code} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-gray-400 text-xs">{i + 1}</td>
                        <td className="px-4 py-3">
                          <span className="font-mono text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">{row.salesman_code}</span>
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-900">{row.salesman_name || '-'}</td>
                        <td className="px-4 py-3 text-gray-500 text-xs">{row.branch_code || '-'}</td>
                        <td className="px-4 py-3 text-right">
                          <span className={`inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                            row.customer_count >= 100 ? 'bg-blue-100 text-blue-800'
                            : row.customer_count >= 50 ? 'bg-green-100 text-green-800'
                            : row.customer_count >= 10 ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-600'
                          }`}>
                            {formatNumber(row.customer_count)} ราย
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-gray-700">{formatNumber(row.total_invoices)}</td>
                        <td className="px-4 py-3 text-right text-gray-700 font-medium">{row.total_amount > 0 ? formatAmount(row.total_amount) : '-'}</td>
                        <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{formatDate(row.last_sale_date)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50 border-t border-gray-200">
                    <tr>
                      <td colSpan={4} className="px-4 py-3 text-sm font-semibold text-gray-700">รวม {filtered.length} คน</td>
                      <td className="px-4 py-3 text-right font-bold text-gray-900">{formatNumber(totalCustomers)} ราย</td>
                      <td className="px-4 py-3 text-right font-bold text-gray-900">{formatNumber(totalInvoices)}</td>
                      <td className="px-4 py-3 text-right font-bold text-gray-900">{formatAmount(filtered.reduce((acc, r) => acc + r.total_amount, 0))}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* ─── TAB 2: ลูกค้าหาย 3 เดือน ─── */}
      {activeTab === 'inactive' && (
        <>
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="ค้นหาชื่อ/รหัสลูกค้า หรือชื่อเซลล์..."
                value={inactiveSearch}
                onChange={e => setInactiveSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={handleExportInactive}
              disabled={detailLoading || inactiveRows.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
            >
              <Download className="w-4 h-4" />
              Export Excel
            </button>
          </div>

          {detailError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
              เกิดข้อผิดพลาด: {detailError}
            </div>
          )}

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {detailLoading ? (
              <div className="flex items-center justify-center py-20">
                <RefreshCw className="w-8 h-8 text-orange-500 animate-spin" />
                <span className="ml-3 text-gray-600">กำลังโหลดข้อมูล...</span>
              </div>
            ) : detailData && inactiveRows.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                <AlertCircle className="w-12 h-12 mb-3 text-green-400" />
                <p className="text-lg font-medium">ไม่พบลูกค้าที่หายเกิน 90 วัน</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                {inactiveRows.length > 0 && (
                  <div className="px-4 py-2 bg-orange-50 border-b border-orange-100 text-sm text-orange-700">
                    พบ <strong>{formatNumber(inactiveRows.length)}</strong> รายที่ไม่มียอดซื้อใน 90 วันที่ผ่านมา
                  </div>
                )}
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 w-12">#</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 whitespace-nowrap">รหัสลูกค้า</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">ชื่อลูกค้า</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 whitespace-nowrap">จังหวัด</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 whitespace-nowrap">เบอร์ติดต่อ</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 whitespace-nowrap">เซลล์ล่าสุด</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 whitespace-nowrap">ซื้อล่าสุด</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 whitespace-nowrap">ห่างกี่วัน</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {inactiveRows.map((row, i) => (
                      <tr key={row.customer_code} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-gray-400 text-xs">{i + 1}</td>
                        <td className="px-4 py-3">
                          <span className="font-mono text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">{row.customer_code}</span>
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-900 text-xs">{row.customer_name || '-'}</td>
                        <td className="px-4 py-3 text-gray-500 text-xs">{row.province || '-'}</td>
                        <td className="px-4 py-3 text-gray-500 text-xs">{row.phone || '-'}</td>
                        <td className="px-4 py-3 text-xs">
                          <span className="font-mono text-xs bg-gray-100 text-gray-700 px-1.5 py-0.5 rounded">{row.last_salesman_code || '-'}</span>
                          <span className="ml-1 text-gray-600">{row.last_salesman_name || ''}</span>
                        </td>
                        <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{formatDate(row.last_sale_date)}</td>
                        <td className="px-4 py-3 text-right">
                          <span className={`inline-flex items-center justify-center px-2 py-0.5 rounded text-xs font-semibold ${
                            row.days_inactive >= 365 ? 'bg-red-100 text-red-700'
                            : row.days_inactive >= 180 ? 'bg-orange-100 text-orange-700'
                            : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {formatNumber(row.days_inactive)} วัน
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {/* ─── TAB 3: มีเครดิตไม่ซื้อ ─── */}
      {activeTab === 'credit' && (
        <>
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="ค้นหาชื่อ/รหัสลูกค้า หรือชื่อเซลล์..."
                value={creditSearch}
                onChange={e => setCreditSearch(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <button
              onClick={handleExportCredit}
              disabled={creditLoading || creditFiltered.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
            >
              <Download className="w-4 h-4" />
              Export Excel
            </button>
          </div>

          {creditError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm">
              เกิดข้อผิดพลาด: {creditError}
            </div>
          )}

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {creditLoading ? (
              <div className="flex items-center justify-center py-20">
                <RefreshCw className="w-8 h-8 text-red-500 animate-spin" />
                <span className="ml-3 text-gray-600">กำลังโหลดข้อมูล...</span>
              </div>
            ) : creditData && creditFiltered.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                <CreditCard className="w-12 h-12 mb-3 text-green-400" />
                <p className="text-lg font-medium">ไม่พบลูกค้าที่มีเครดิตแต่ไม่ซื้อ</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                {creditData && (
                  <div className="px-4 py-2 bg-red-50 border-b border-red-100 text-sm text-red-700">
                    พบ <strong>{formatNumber(creditFiltered.length)}</strong> รายที่มีวงเงินเครดิต
                  </div>
                )}
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 w-12">#</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 whitespace-nowrap">รหัสลูกค้า</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">ชื่อลูกค้า</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 whitespace-nowrap">จังหวัด</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 whitespace-nowrap">เบอร์ติดต่อ</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 whitespace-nowrap">ประเภท</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 whitespace-nowrap">วงเงินเครดิต</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 whitespace-nowrap">วงเงินคงเหลือ</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {creditFiltered.map((row, i) => (
                      <tr key={row.customer_code} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-gray-400 text-xs">{i + 1}</td>
                        <td className="px-4 py-3">
                          <span className="font-mono text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">{row.customer_code}</span>
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-900 text-xs">{row.customer_name || '-'}</td>
                        <td className="px-4 py-3 text-gray-500 text-xs">{row.province || '-'}</td>
                        <td className="px-4 py-3 text-gray-500 text-xs">{row.phone || '-'}</td>
                        <td className="px-4 py-3 text-xs">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${
                            row.customer_type === 'ma' ? 'bg-purple-100 text-purple-700'
                            : row.customer_type === 'new' ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-600'
                          }`}>
                            {row.customer_type === 'ma' ? 'ตัวแทนรายใหญ่' : row.customer_type === 'new' ? 'ลูกค้าใหม่' : 'ทั่วไป'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-medium text-gray-900 text-xs">{formatAmount(row.credit_limit)}</td>
                        <td className="px-4 py-3 text-right text-xs">
                          <span className={row.credit_remaining < row.credit_limit * 0.2 ? 'text-red-600 font-medium' : 'text-gray-700'}>
                            {formatAmount(row.credit_remaining)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SalesmanCustomerCountComponent;
